import React, { useState } from 'react';
import {
  Menu,
  MenuTrigger,
  MenuPopover,
  MenuList,
  MenuItem,
  MenuDivider,
  Button,
  Avatar,
  Text,
  makeStyles,
  mergeClasses,
  shorthands,
} from '@fluentui/react-components';
import {
  SignOutRegular,
  ShieldTaskRegular,
} from '@fluentui/react-icons';
import { useMSALAuth } from '../../contexts/MSALAuthContext';
import type { UserProfileMenuProps } from '../../types/layout';

const useStyles = makeStyles({
  userButton: {
    color: '#ffffff',
    backgroundColor: 'transparent',
    ...shorthands.border('none'),
    '&:hover': {
      backgroundColor: 'rgba(255, 255, 255, 0.1)',
    },
  },
  userButtonCompact: {
    minWidth: '28px',
    minHeight: '28px',
    width: '28px',
    height: '28px',
    ...shorthands.padding('0'),
  },
  userInfo: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    ...shorthands.padding('8px', '12px'),
  },
  userName: {
    fontWeight: '600',
    fontSize: '14px',
  },
  userEmail: {
    fontSize: '12px',
    color: '#666666',
  },
  adminBadge: {
    display: 'flex',
    alignItems: 'center',
    ...shorthands.gap('4px'),
    fontSize: '12px',
    color: '#107c10',
    fontWeight: '500',
    width: 'max-content',
  },
});

export const UserProfileMenu: React.FC<UserProfileMenuProps> = ({ compact = false }) => {
  const styles = useStyles();
  const { user, logout } = useMSALAuth();
  const [isOpen, setIsOpen] = useState(false);

  const handleLogout = () => {
    setIsOpen(false);
    logout();
  };

  if (!user) {
    return null;
  }

  // Get initials for avatar
  const getInitials = (name: string, email: string) => {
    if (name) {
      const nameParts = name.split(' ');
      if (nameParts.length >= 2) {
        return `${nameParts[0][0]}${nameParts[1][0]}`.toUpperCase();
      }
      return name[0].toUpperCase();
    }
    if (email && email.length > 0) {
      return email[0].toUpperCase();
    }
    return 'U'; // Fallback for User
  };

  return (
    <Menu open={isOpen} onOpenChange={(e, data) => setIsOpen(data.open)} data-testid="user-profile-menu-root">
      <MenuTrigger disableButtonEnhancement data-testid="user-profile-menu-trigger-wrapper">
        <Button
          appearance="transparent"
          className={mergeClasses(styles.userButton, compact && styles.userButtonCompact)}
          aria-label="User profile menu"
          data-testid="user-profile-menu-trigger"
        >
          <Avatar
            name={user.name || user.email}
            size={compact ? 20 : 28}
            initials={getInitials(user.name, user.email)}
            color="brand"
            data-testid="user-profile-menu-avatar"
          />
        </Button>
      </MenuTrigger>

      <MenuPopover data-testid="user-profile-menu-popover">
        <MenuList data-testid="user-profile-menu">
          <div className={styles.userInfo} data-testid="user-profile-info">
            <Text className={styles.userName} data-testid="user-profile-name">
              {user.name || 'User'}
            </Text>
            <Text className={styles.userEmail} data-testid="user-profile-email">
              {user.email}
            </Text>
            {user.isTeamsAdmin && (
              <>
                {user.directoryRoles && user.directoryRoles.length > 0 ? (
                  user.directoryRoles.map((role) => (
                    <div key={role} className={styles.adminBadge} data-testid="user-profile-admin-badge">
                      <ShieldTaskRegular fontSize={12} />
                      {role}
                    </div>
                  ))
                ) : (
                  <div className={styles.adminBadge} data-testid="user-profile-admin-badge">
                    <ShieldTaskRegular fontSize={12} />
                    No role
                  </div>
                )}
              </>
            )}
          </div>
          
          <MenuDivider />
          
          <MenuItem
            icon={<SignOutRegular />}
            onClick={handleLogout}
            data-testid="sign-out-button"
          >
            Sign Out
          </MenuItem>
        </MenuList>
      </MenuPopover>
    </Menu>
  );
};